import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../config/api'
import { useAuth } from './AuthContext'

export type SettingValue = string | number | boolean | string[] | Record<string, unknown> | null

export interface SettingDefinition {
  key: string
  category: string
  label: string
  description?: string
  type: 'STRING' | 'INTEGER' | 'DECIMAL' | 'BOOLEAN' | 'ENUM' | 'MULTI_ENUM' | 'JSON'
  defaultValue: SettingValue
  options?: Array<{ value: string; label: string }>
  min?: number
  max?: number
  personal?: boolean
  public?: boolean
}

interface SettingsContextValue {
  effective: Record<string, SettingValue>
  catalog: SettingDefinition[]
  loading: boolean
  refresh: () => Promise<void>
  get: <T extends SettingValue>(key: string, fallback: T) => T
  savePreferences: (values: Record<string, SettingValue>) => Promise<void>
  saveApplication: (
    values: Record<string, SettingValue>,
    scope?: 'GLOBAL' | 'CUSTOMER',
    customerId?: number | null
  ) => Promise<void>
}

const LOCAL_KEY = 'fleet_settings_fallback'
const DEFAULTS: Record<string, SettingValue> = {
  'appearance.theme': 'light',
  'appearance.locale': 'en',
  'appearance.compact_mode': false,
  'appearance.reduced_motion': false,
  'appearance.timezone': 'UTC',
  'workspace.default_view': 'dashboard',
  'workspace.window_mode': 'mac',
  'workspace.map_style': 'streets',
  'workspace.map_center_latitude': 14.7167,
  'workspace.map_center_longitude': -17.4677,
  'workspace.map_default_zoom': 12,
  'workspace.cluster_markers': true,
  'refresh.dashboard_seconds': 30,
  'refresh.map_seconds': 30,
  'tracking.live_threshold_minutes': 60,
  'tracking.stale_threshold_hours': 24,
  'branding.app_name': 'SiniFleet',
  'branding.logo_url': '',
  'branding.default_locale': 'en',
  'branding.primary_color': '#2563eb',
  'branding.report_legal_line': '(c) {year} Sini Technology - SiniFleet. All rights reserved.',
  'alerts.default_severity': 'WARNING',
  'alerts.default_channels': ['IN_APP'],
  'alerts.default_recipient_roles': ['ADMIN', 'OPERATOR'],
  'alerts.default_cooldown_seconds': 300,
  'missions.default_priority': 'MEDIUM',
  'missions.default_duration_minutes': 480,
  'features.show_alerts': true,
  'features.show_missions': true,
  'features.show_staff': true,
  'features.show_geofences': true,
  'display.max_table_rows': 100,
  'display.trajectory_max_points': 5000,
  'retention.audit_days': 365,
  'retention.alert_history_days': 90,
  'notifications.in_app': true,
  'notifications.email': true
}

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined)

const asValues = (payload: any): Record<string, SettingValue> => {
  if (!payload) return {}
  if (payload.values && typeof payload.values === 'object') return payload.values
  if (payload.settings && typeof payload.settings === 'object') return payload.settings
  if (payload.data?.values) return payload.data.values
  if (payload.data?.settings) return payload.data.settings
  if (Array.isArray(payload)) {
    return Object.fromEntries(payload.map(item => [item.key, item.effectiveValue ?? item.value]))
  }
  return typeof payload === 'object' ? payload : {}
}

const asCatalog = (payload: any): SettingDefinition[] => {
  const rows = payload?.catalog || payload?.data?.catalog || payload?.data || payload
  return Array.isArray(rows) ? rows.map((row: any) => ({
    key: row.key,
    category: row.category || row.group || 'General',
    label: row.label || row.key,
    description: row.description,
    type: row.type || row.valueType || 'STRING',
    defaultValue: row.defaultValue ?? row.default_value ?? null,
    options: row.options,
    min: row.min,
    max: row.max,
    personal: row.personal === true || row.scope === 'USER',
    public: row.public === true
  })) : []
}

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth()
  const { i18n } = useTranslation()
  const [effective, setEffective] = useState<Record<string, SettingValue>>(() => {
    try {
      return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}') }
    } catch {
      return { ...DEFAULTS }
    }
  })
  const [catalog, setCatalog] = useState<SettingDefinition[]>([])
  const [loading, setLoading] = useState(true)

  const applyClientSettings = useCallback((values: Record<string, SettingValue>) => {
    const theme = String(values['appearance.theme'] || 'light')
    const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    document.documentElement.classList.toggle('dark', theme === 'dark' || (theme === 'system' && systemDark))
    document.documentElement.dataset.density = values['appearance.compact_mode'] === true ? 'compact' : 'comfortable'
    document.documentElement.dataset.reduceMotion = values['appearance.reduced_motion'] === true ? 'true' : 'false'
    const language = String(values['appearance.locale'] || '')
    if (language && !i18n.language?.startsWith(language)) i18n.changeLanguage(language)
  }, [i18n])

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const [publicResult, effectiveResult, catalogResult] = await Promise.all([
        apiClient.get('/settings/public').catch(() => ({ data: {} })),
        isAuthenticated ? apiClient.get('/settings/effective').catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        isAuthenticated ? apiClient.get('/settings/catalog').catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ])
      const next = {
        ...DEFAULTS,
        ...asValues(publicResult.data),
        ...asValues(effectiveResult.data)
      }
      setEffective(next)
      setCatalog(asCatalog(catalogResult.data))
      localStorage.setItem(LOCAL_KEY, JSON.stringify(next))
      applyClientSettings(next)
    } finally {
      setLoading(false)
    }
  }, [applyClientSettings, isAuthenticated])

  useEffect(() => { refresh() }, [refresh])

  const savePreferences = useCallback(async (values: Record<string, SettingValue>) => {
    await apiClient.patch('/settings/preferences', { values })
    await refresh()
  }, [refresh])

  const saveApplication = useCallback(async (
    values: Record<string, SettingValue>,
    scope: 'GLOBAL' | 'CUSTOMER' = 'CUSTOMER',
    customerId?: number | null
  ) => {
    await apiClient.patch('/settings/application', { values, scope, customerId: customerId ?? null })
    await refresh()
  }, [refresh])

  const value = useMemo<SettingsContextValue>(() => ({
    effective,
    catalog,
    loading,
    refresh,
    get: <T extends SettingValue>(key: string, fallback: T): T =>
      (effective[key] === undefined ? fallback : effective[key]) as T,
    savePreferences,
    saveApplication
  }), [catalog, effective, loading, refresh, saveApplication, savePreferences])

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
}

export const useSettings = (): SettingsContextValue => {
  const value = useContext(SettingsContext)
  if (!value) throw new Error('useSettings must be used within SettingsProvider')
  return value
}

export default SettingsContext
